import clsx from 'clsx';
import type { FC } from 'react';
import React, { useState, useEffect, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { createUseStyles, useTheme } from 'react-jss';
import { LocaleContext } from '../contexts/LocaleContext';
import { SelectionContext } from '../contexts/SelectionContext';
import { MOBILE, ViewModeContext } from '../contexts/ViewModeContext';
import { useQrCode } from '../hooks/useQrCode';
import { Coconut } from './KioskIcons';
import { KioskIdle } from './KioskIdle';
import { KioskLandingChoices } from './KioskLandingChoices';

export const FOOTER_HEIGHT = '8rem';
export const IDLE_DURATION = 30000;

const useStyles = createUseStyles((theme: any) => ({
  bgDarkPrimary: {
    background: theme.palette.primary[600],
  },
  bgPrimary: {
    background: theme.palette.primary[400],
  },
  footerHeight: {
    height: FOOTER_HEIGHT,
  },
  rootTextColor: {
    color: theme.palette.contrastText,
  },
  mainHeight: {
    height: `calc(100vh - ${FOOTER_HEIGHT})`,
  },
  logo: {
    maxHeight: '3.5rem',
  },
  qrCode: {
    aspectRatio: '1 / 1',
    height: '100%',
  },
  fadeIn: {
    animation: '$zoomFadeIn 0.25s ease-out forwards',
    opacity: 0,
  },
  '@keyframes zoomFadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
}));

export const EnhancedKioskLanding: FC = () => {
  const classes = useStyles({ theme: useTheme() });
  const mode = useContext(ViewModeContext);
  const isMobile = mode === MOBILE;
  const [isIdleVisible, setIsIdleVisible] = useState(!isMobile);
  const [idleTimer, setIdleTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const resetTimer = () => {
      setIsIdleVisible(false);
      if (idleTimer) {
        clearTimeout(idleTimer);
      }

      const newTimer = setTimeout(() => {
        setIsIdleVisible(true);
      }, IDLE_DURATION);

      setIdleTimer(newTimer);
    };

    window.addEventListener('mouseup', resetTimer);
    window.addEventListener('touchend', resetTimer);

    return () => {
      window.removeEventListener('mouseup', resetTimer);
      window.removeEventListener('touchend', resetTimer);

      if (idleTimer) {
        clearTimeout(idleTimer);
      }
    };
  }, [idleTimer]);

  return (
    <div
      className={clsx(
        classes.bgDarkPrimary,
        classes.rootTextColor,
        'flex flex-col justify-center items-center',
      )}
    >
      <main
        className={clsx(
          classes.bgPrimary,
          classes.mainHeight,
          'w-full flex flex-col gap-11 overflow-auto rounded-b-3xl justify-center',
        )}
      >
        <Header />
        {isIdleVisible ? <KioskIdle /> : <KioskLandingChoices />}
      </main>
      <Footer />
    </div>
  );
};

const Header: FC = () => {
  const classes = useStyles({ theme: useTheme() });

  const { logo } = (window as any).state.theme;
  const vendorName = (window as any).state.vendor_name;

  return (
    <div
      className={clsx('w-full flex items-center sm:items-end justify-center')}
    >
      <div className="bg-white-100 px-8 py-3 rounded-full max-w-66 sm:min-w-40 sm:max-w-xs sm:min-h-20 mx-12 flex items-center justify-center">
        {logo !== null ? (
          <img
            alt={vendorName}
            className={classes.logo}
            data-testid="logo"
            src={logo}
          />
        ) : (
          <h1 className="text-3xl leading-10 font-semibold text-black-90 text-center">
            {vendorName}
          </h1>
        )}
      </div>
    </div>
  );
};

const Footer: FC = () => {
  const intl = useIntl();
  const classes = useStyles({ theme: useTheme() });
  const [{ location }] = useContext(SelectionContext);
  const [locale] = useContext(LocaleContext);

  const { data: qr, isFetching } = useQrCode({
    locationId: `${location.id}`,
    language: locale,
  });

  return (
    <div
      className={clsx(
        classes.bgDarkPrimary,
        classes.footerHeight,
        'w-full flex items-center justify-between py-3 px-7',
      )}
    >
      <div className="relative h-full w-fit flex gap-4 items-center">
        <div
          className={clsx(
            classes.qrCode,
            'bg-white-100 rounded-lg flex items-center justify-center',
          )}
        >
          {qr && !isFetching ? (
            <img
              alt={intl.formatMessage({ id: 'Landing.kiosk.qr_code.alt' })}
              className={clsx(
                classes.fadeIn,
                'relative bg-white-100 rounded-lg h-full w-full',
              )}
              src={typeof qr.data === 'string' ? qr.data : ''}
            />
          ) : (
            <Coconut />
          )}
        </div>
        <div className="flex flex-col items-start gap-2 font-semibold">
          <span className={clsx(classes.bgPrimary, 'px-7 py-2 rounded-lg')}>
            <FormattedMessage id="Landing.kiosk.qr_code.scan" />
          </span>
          <span className="inline-block max-w-48 leading-5">
            <FormattedMessage id="Landing.kiosk.qr_code.manage" />
          </span>
        </div>
      </div>
    </div>
  );
};
